import { useCallback, useEffect, useRef } from "react";
import { normalizeRect, rectsOverlap, Rect } from "./marquee";
import { useSeatPlanStore } from "@/stores/v2/seatPlanStore";

/**
 * Pointer/keyboard handlers for marquee selection on the grid container.
 * Each SELECTABLE filled seat carries data-seat-student="<qoriaUserId>"; claimed seats
 * omit it and are therefore never hit.
 *
 * Hold Cmd/Ctrl while dragging to TOGGLE the seats inside the rectangle against the
 * existing selection: unselected seats are added, already-selected seats are removed,
 * and seats outside the rectangle keep their current state. Without the modifier the
 * marquee replaces the selection with whatever it covers.
 *
 * The marquee starts from anywhere in the grid (gaps, padding, empty seats, and even a
 * card body) EXCEPT on the interactive controls below, which need the raw click. The
 * `.clickable` class flags in-card affordances (screenshot live-view, close-tab, tab rows)
 * so their click reaches the card instead of starting a marquee, as does the release-claim
 * button, which is a plain div. Hit detection still relies on data-seat-student, so only
 * selectable cards get picked up.
 *
 * It can also be started from elements outside the grid that are tagged with
 * data-seatplan-marquee-zone (e.g. the toolbar bar above the grid). A document-level
 * pointerdown listener picks those up so the otherwise-dead toolbar strip becomes a valid
 * start surface; hit detection still queries the grid container for seats.
 *
 * Once a drag begins the pointer is captured on the grid container and move/up/cancel are
 * tracked on `window`, so the rectangle keeps expanding and ALWAYS tears down on release,
 * even when the cursor is over interactive card content, leaves the grid, or the browser
 * tries to hijack the gesture with a native drag.
 */
const INTERACTIVE_SELECTOR =
	"input, button, a, select, textarea, label, [role='button'], [role='checkbox'], .chakra-checkbox, .clickable, .release_claim_button";
const MARQUEE_ZONE_SELECTOR = "[data-seatplan-marquee-zone]";

interface SeatSelectionHandlers {
	onPointerDown: (e: React.PointerEvent) => void;
}

export function useSeatSelection(
	containerRef: React.RefObject<HTMLDivElement>,
	marqueeRef: React.RefObject<HTMLDivElement>,
	enabled: boolean
): SeatSelectionHandlers {
	const setSelection = useSeatPlanStore((s) => s.setSelection);
	const cleanup = useRef<(() => void) | null>(null);

	// Draw the selection rectangle by mutating a persistent DOM node directly. Writing it to
	// the store on every frame would re-render SeatPlanView (and its mounted-but-closed Focus
	// drawer / modals) ~60 times a second during a drag, which was the remaining CPU spike.
	const drawMarquee = useCallback(
		(rect: Rect | null) => {
			const box = marqueeRef.current;
			if (!box) return;
			if (!rect) {
				box.style.display = "none";
				return;
			}
			box.style.display = "block";
			box.style.left = `${rect.x}px`;
			box.style.top = `${rect.y}px`;
			box.style.width = `${rect.w}px`;
			box.style.height = `${rect.h}px`;
		},
		[marqueeRef]
	);

	useEffect(() => () => cleanup.current?.(), []);

	const beginMarquee = useCallback(
		(anchorX: number, anchorY: number, additive: boolean, pointerId: number) => {
			const container = containerRef.current;
			if (!container) return;

			const baseSelection = additive ? useSeatPlanStore.getState().selectedStudents : [];
			const baseSet = new Set(baseSelection);

			// Snapshot each selectable seat's element + rectangle ONCE at drag start. Previously
			// every pointermove called getBoundingClientRect() for every seat, forcing a
			// synchronous layout/reflow on each move - the main driver of the drag-time CPU
			// spike. Cached rects make each move pure arithmetic.
			const seats: { id: string; el: HTMLElement; rect: Rect }[] = [];
			container.querySelectorAll<HTMLElement>("[data-seat-student]").forEach((el) => {
				const id = el.getAttribute("data-seat-student");
				if (!id) return;
				const b = el.getBoundingClientRect();
				seats.push({ id, el, rect: { x: b.left, y: b.top, w: b.width, h: b.height } });
			});

			// Track the selection we last pushed to the store so we only write when the hit set
			// actually changes (i.e. the rectangle crosses a seat edge), not on every frame.
			// That keeps the seat checkboxes ticking live during the drag while updating the
			// store only a handful of times per drag - cheap enough to avoid the repaint storm,
			// especially with the classroom now keyed off "has selection" rather than the count.
			let applied = useSeatPlanStore.getState().selectedStudents;
			const selectionChanged = (a: string[], b: string[]): boolean => {
				if (a.length !== b.length) return true;
				const set = new Set(a);
				return b.some((id) => !set.has(id));
			};

			// Capture the pointer on the (always-present) grid container so EVERY subsequent
			// pointer event for this drag is delivered here, no matter what is underneath the
			// cursor. Without this, releasing over interactive card content (buttons, Chakra
			// Tooltip/Popover wrappers, the student tile) or letting a native drag/gesture
			// take over could swallow the release, leaving the marquee stuck on. jsdom and
			// older browsers may not implement pointer capture, so it is best-effort.
			const captureTarget = container;
			try {
				captureTarget.setPointerCapture?.(pointerId);
			} catch {
				// Pointer capture is an optimisation; the window listeners below still work without it.
			}

			// Recompute the marquee rectangle + hit set for a pointer position. Pulled out so
			// both the throttled frame and the final release can run it.
			const applyAt = (clientX: number, clientY: number) => {
				const rect = normalizeRect(anchorX, anchorY, clientX, clientY);
				drawMarquee(rect);

				const hitSet = new Set<string>();
				for (const seat of seats) {
					if (rectsOverlap(rect, seat.rect)) hitSet.add(seat.id);
				}

				let next: string[];
				if (!additive) {
					next = [...hitSet];
				} else {
					// Toggle: flip the state of seats inside the rectangle, keep everything outside it.
					next = baseSelection.filter((id) => !hitSet.has(id));
					hitSet.forEach((id) => {
						if (!baseSet.has(id)) next.push(id);
					});
				}

				if (selectionChanged(applied, next)) {
					applied = next;
					setSelection(next);
				}
			};

			// Coalesce pointermove events to at most one update per animation frame. High-rate
			// pointing devices fire moves far faster than the display refreshes (often 120-1000Hz),
			// and doing the work on every raw event was still spiking the CPU even over empty
			// space. We stash the latest coordinates and process them once per frame.
			let scheduled = false;
			let frameId: number | null = null;
			let lastX = anchorX;
			let lastY = anchorY;
			const onMove = (ev: PointerEvent) => {
				lastX = ev.clientX;
				lastY = ev.clientY;
				if (scheduled) return;
				scheduled = true;
				frameId = requestAnimationFrame(() => {
					scheduled = false;
					frameId = null;
					applyAt(lastX, lastY);
				});
			};

			// Seat cards render images (avatars / live screenshots) which are natively
			// draggable. On trackpads especially, a click-drag can start a native HTML5
			// image drag, after which the browser emits dragstart/dragend instead of move/up
			// events, so the marquee would stay stuck on. Block native drag for the lifetime
			// of the marquee so the pointer events keep flowing.
			const onDragStart = (ev: Event) => ev.preventDefault();

			const onEnd = () => {
				window.removeEventListener("pointermove", onMove, true);
				window.removeEventListener("pointerup", onEnd, true);
				window.removeEventListener("pointercancel", onEnd, true);
				window.removeEventListener("dragstart", onDragStart, true);
				try {
					captureTarget.releasePointerCapture?.(pointerId);
				} catch {
					// Capture may already be gone (e.g. pointercancel released it); ignore.
				}
				// If a move was queued but its frame had not fired yet, settle on the final
				// position so the committed selection matches where the pointer was released.
				if (scheduled) {
					scheduled = false;
					if (frameId !== null) cancelAnimationFrame(frameId);
					frameId = null;
					applyAt(lastX, lastY);
				}
				cleanup.current = null;
				drawMarquee(null);
				// Selection was applied live during the drag (only when it changed), so there is
				// nothing to commit here. A bare click never moves, so it never alters selection.
			};

			// Listen in the CAPTURE phase so a descendant calling stopPropagation() cannot
			// block these, and include pointercancel so the marquee always tears down even if
			// the browser/OS hijacks the gesture (which is what was leaving it stuck before).
			window.addEventListener("pointermove", onMove, true);
			window.addEventListener("pointerup", onEnd, true);
			window.addEventListener("pointercancel", onEnd, true);
			window.addEventListener("dragstart", onDragStart, true);
			cleanup.current = onEnd;
		},
		[containerRef, drawMarquee, setSelection]
	);

	const onPointerDown = useCallback(
		(e: React.PointerEvent) => {
			if (!enabled || e.button !== 0 || !containerRef.current) return;
			// Empty seats carry role="button" for keyboard moves only, so they stay a marquee start surface.
			const control = (e.target as HTMLElement).closest(INTERACTIVE_SELECTOR);
			if (control && !control.hasAttribute("data-seat-empty")) return; // let controls (checkbox, buttons) handle the click
			// Filled seats are drag sources; starting a marquee here would steal the pointer from dnd-kit.
			if ((e.target as HTMLElement).closest("[data-seat-student]")) return;
			// Stop the browser from beginning a native text selection while the marquee is drawn.
			e.preventDefault();
			beginMarquee(e.clientX, e.clientY, e.metaKey || e.ctrlKey, e.pointerId);
		},
		[enabled, containerRef, beginMarquee]
	);

	// Allow the marquee to start from tagged zones outside the grid (e.g. the toolbar bar
	// above it), so its empty space is no longer a dead strip. The grid keeps its own
	// React onPointerDown, so we ignore presses inside the grid here to avoid double-starting.
	useEffect(() => {
		if (!enabled) return;
		const onDocPointerDown = (e: PointerEvent) => {
			if (e.button !== 0 || !containerRef.current) return;
			const target = e.target as HTMLElement | null;
			if (!target || typeof target.closest !== "function") return;
			if (!target.closest(MARQUEE_ZONE_SELECTOR)) return;
			if (containerRef.current.contains(target)) return;
			if (target.closest(INTERACTIVE_SELECTOR)) return;
			e.preventDefault();
			beginMarquee(e.clientX, e.clientY, e.metaKey || e.ctrlKey, e.pointerId);
		};
		document.addEventListener("pointerdown", onDocPointerDown);
		return () => document.removeEventListener("pointerdown", onDocPointerDown);
	}, [enabled, containerRef, beginMarquee]);

	return { onPointerDown };
}
